import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type RangeSelection,
  LexicalNode,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from "@lexical/list";
import { $setBlocksType } from "@lexical/selection";
import { SelectedIcon } from "../../Icons";

import type { ToolbarAction, ToolbarItem, ActiveFormats } from "./types";
import { TOOLBAR_ITEMS, DEFAULT_ACTIVE_FORMATS } from "./types";
import { getActiveFormats } from "./getActiveFormats";
import { useLinkModal } from "./hooks/useLinkModal";
import { ToolbarRegion, ToolbarItems, ToolbarDivider, ToolbarButton, ToolbarIcon, ToolbarText } from "./Styles";

interface ToolbarProps {
  baseId: string;
  baseTestId: string;
  enabledActions: ToolbarAction[];
  disabled?: boolean;
  ariaControls: string;
}

export const Toolbar = (props: ToolbarProps): React.ReactElement => {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>(DEFAULT_ACTIVE_FORMATS);
  const { openLinkModal, linkModalElement } = useLinkModal({
    editor,
    baseId: props.baseId,
    baseTestId: props.baseTestId,
  });

  // Reads which formats (bold, heading, etc.) are active at the cursor position
  const syncActiveFormats = (): void => {
    editor.getEditorState().read((): void => {
      setActiveFormats(getActiveFormats());
    });
  };

  // Resets toolbar button highlights when the editor loses focus
  const resetActiveFormats = (): void => {
    setActiveFormats(DEFAULT_ACTIVE_FORMATS);
  };

  // Highlights toolbar buttons to reflect formatting at the cursor position
  useEffect((): (() => void) => {
    // Re-sync when content changes (e.g. user applies bold)
    const unregisterUpdate: () => void = editor.registerUpdateListener(syncActiveFormats);
    // Re-sync when cursor moves to differently formatted text
    const unregisterSelection: () => void = editor.registerCommand(
      SELECTION_CHANGE_COMMAND, (): boolean => {
        syncActiveFormats();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
    // Reset highlights when focus leaves the editor
    const rootElement: HTMLElement = editor.getRootElement()!;
    rootElement.addEventListener("blur", resetActiveFormats);

    syncActiveFormats();

    return (): void => {
      unregisterUpdate();
      unregisterSelection();
      rootElement.removeEventListener("blur", resetActiveFormats);
    };
  }, [editor]);

  const toggleHeading = (): void => {
    editor.update((): void => {
      const selection: RangeSelection | null = $getSelection() as RangeSelection | null;
      if (!selection || !$isRangeSelection(selection)) {
        return;
      }
      const anchorNode: LexicalNode = selection.anchor.getNode();
      const topLevelElement: LexicalNode | null = anchorNode.getTopLevelElement();
      if (
        topLevelElement &&
        $isHeadingNode(topLevelElement) &&
        topLevelElement.getTag() === "h2"
      ) {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createHeadingNode("h2"));
      }
    });
  };

  const handleAction = (action: ToolbarItem["action"]): void => {
    switch (action) {
      case "heading":
        toggleHeading();
        break;
      case "bold":
      case "highlight":
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        break;
      case "list":
        if (activeFormats.list) {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
        break;
      case "insert-link":
        openLinkModal();
        break;
      default:
        break;
    }
  };

  // A button is disabled if the whole toolbar is disabled, or its action isn't in enabledActions
  const isButtonDisabled = (item: ToolbarItem): boolean => {
    return Boolean(props.disabled || !props.enabledActions.includes(item.action));
  };

  // Ref map for arrow key navigation between toolbar buttons
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Arrow keys move focus between enabled toolbar buttons, Tab skips to the textarea
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const isForward: boolean = event.key === "ArrowRight" || event.key === "ArrowDown";
    const isBackward: boolean = event.key === "ArrowLeft" || event.key === "ArrowUp";

    if (!isForward && !isBackward) {
      return;
    }

    const enabledIds: string[] = TOOLBAR_ITEMS
      .filter((item: ToolbarItem): boolean => !isButtonDisabled(item))
      .map((item: ToolbarItem): string => item.id);

    const focusedId: string | undefined = [...buttonRefs.current.entries()]
      .find(([, element]): boolean => element === document.activeElement)?.[0];

    const currentIndex: number = enabledIds.indexOf(focusedId ?? "");

    event.preventDefault();

    const nextIndex: number = isForward
      ? (currentIndex + 1) % enabledIds.length
      : (currentIndex - 1 + enabledIds.length) % enabledIds.length;

    buttonRefs.current.get(enabledIds[nextIndex])?.focus();
  };

  const isButtonActive = (item: ToolbarItem): boolean => {
    if (isButtonDisabled(item)) {
      return false;
    }

    if (item.action === "bold" || item.action === "highlight") {
      return activeFormats.bold;
    }

    return Boolean(
      (item.action === "heading" && activeFormats.heading) ||
      (item.action === "list" && activeFormats.list)
    );
  };

  return (
    <ToolbarRegion
      data-testid={`${props.baseTestId}-toolbar-test`}
      role="toolbar"
      aria-label="Text formatting"
      aria-controls={props.ariaControls}
      onKeyDown={handleKeyDown}
    >
      <ToolbarItems data-testid={`${props.baseTestId}-toolbar-items-test`}>
        {TOOLBAR_ITEMS.map((item: ToolbarItem, index: number) => {
          const isFirstEnabledButton: boolean = index === TOOLBAR_ITEMS.findIndex(
            (toolbarItem: ToolbarItem): boolean => !isButtonDisabled(toolbarItem)
          )

          return (
            <Fragment key={item.id}>
              <ToolbarButton
                ref={(element: HTMLButtonElement | null): void => {
                  if (element) {
                    buttonRefs.current.set(item.id, element);
                  } else {
                    buttonRefs.current.delete(item.id);
                  }
                }}
                type="button"
                data-testid={`${props.baseTestId}-toolbar-${item.id}-test`}
                aria-label={item.text}
                onClick={(): void => handleAction(item.action)}
                tabIndex={isFirstEnabledButton ? 0 : -1}
                disabled={isButtonDisabled(item)}
                $active={isButtonActive(item)}
              >
              <ToolbarIcon aria-hidden>
                <SelectedIcon iconType={item.iconType} />
              </ToolbarIcon>
              <ToolbarText>{item.text}</ToolbarText>
            </ToolbarButton>
            {item.showDividerAfter && <ToolbarDivider aria-hidden />}
          </Fragment>
          );
        })}
      </ToolbarItems>
      {linkModalElement}
    </ToolbarRegion>
  );
};
